import React, { SyntheticEvent, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Dropdown as DropdownNativo, Grid, Icon, Input } from 'semantic-ui-react';
import { Field, FormikProps } from 'formik';

import Button from '../../../../../../components/Button';
import { FieldLabel } from '../../../../../../styles';
import { AddressService } from '../../../../../../services/address';
import {
  maskCpfOrCnpj,
  maskPhoneNumber,
  maskZipCode
} from '../../../../../../services/masks';
import { CustoDropDownItemProps } from '../../../../../../components/types';
import { InsurerState } from '../../../../../../types/InsurerState';
import { UserState } from '../../../../../../types/UserState';
import { isBackOfficeUser } from '../../../../../../utils/isBackOfficeUser';
import { isLoggedEstateRole } from '../../../../../../utils/isEstateRole';
import { stateOptions } from '../../../../../estates-list/types';
import { getClaimInsurerDropdownOptions } from '../../../../utils/claimInsurerOptions';
import { claimEstatesStatusEnum } from '../../../../constants/ClaimEstatesStatusEnum';
import { OtherProductClaimInitialValuesType } from '../../Formik/types';

type CondominiumBasicInfoStepProps = FormikProps<OtherProductClaimInitialValuesType> & {
  estates: CustoDropDownItemProps[] | null;
  insurers: InsurerState[] | null;
  availableBrokersAndEstates?: any;
  isEditMode?: boolean;
  users?: UserState[];
};

const SectionTitle = styled.div`
  margin-top: 1.5em;
  margin-bottom: 0.6em;
  padding-bottom: 0.35em;
  border-bottom: 1px solid #ddc7f2;
  color: #59249b;
  font-family: 'Open Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 100%;
  letter-spacing: 0%;
`;

const condominiumTypeOptions = [
  { key: 'RESIDENCIAL', value: 'RESIDENCIAL', text: 'Residencial' },
  { key: 'COMERCIAL', value: 'COMERCIAL', text: 'Comercial' },
  { key: 'MISTO', value: 'MISTO', text: 'Misto' }
];

const insuranceStatusOptions = [
  'Renovado (2027)',
  'A Vencer',
  'Em Atualização',
  'N/A',
  'Vencido',
  'Implantação'
].map(value => ({ key: value, value, text: value }));

const CondominiumBasicInfoStep = ({
  values,
  setFieldValue,
  estates,
  insurers,
  availableBrokersAndEstates,
  isEditMode = false,
  users = []
}: CondominiumBasicInfoStepProps) => {
  const [loadingZipCode, setLoadingZipCode] = useState(false);
  const [selectableEstates, setSelectableEstates] = useState<any[]>([]);
  const [responsibleOptions, setResponsibleOptions] = useState<CustoDropDownItemProps[]>(
    []
  );
  const isEstateUser = isLoggedEstateRole();
  const canSelectResponsible = isBackOfficeUser();
  const selectableInsurers = getClaimInsurerDropdownOptions(insurers);
  const condominium = values.claimCondominium;

  useEffect(() => {
    if (!estates?.length) return;

    setSelectableEstates(
      estates.map(estate => ({
        index: estate.value.id,
        value: estate.value.id,
        text: estate.value.name
      }))
    );

    const activeEstateId = availableBrokersAndEstates?.activeEstateId;
    if (estates.length === 1) {
      setFieldValue('claimEstate.estateId', estates[0].value.id);
    } else if (activeEstateId && !isEditMode) {
      const activeEstate = estates.find(e => e.value.id === activeEstateId);
      if (activeEstate) {
        setFieldValue('claimEstate.estateId', activeEstate.value.id);
      }
    }
  }, [estates, availableBrokersAndEstates?.activeEstateId, setFieldValue, isEditMode]);

  useEffect(() => {
    if (!users?.length) return;
    setResponsibleOptions(
      users.map((user, index) => ({
        value: user.id,
        key: index,
        text: user.name
      }))
    );
  }, [users]);

  const onChangeCep = (zipCode: string): void => {
    const maskedValue = maskZipCode(zipCode);
    setFieldValue('claimProperty.zipCode', maskedValue);

    if (maskedValue.length >= 9) {
      setLoadingZipCode(true);
      AddressService.getAddressByZipCode(zipCode)
        .then(address => {
          setFieldValue('claimProperty.city', address.city);
          setFieldValue('claimProperty.district', address.district);
          setFieldValue('claimProperty.state', address.state);
          setFieldValue('claimProperty.street', address.street);
          setFieldValue('claimProperty.country', 'Brasil');
        })
        .catch(error => console.log('address error:', error))
        .finally(() => setLoadingZipCode(false));
    }
  };

  const setCondominiumField = (field: string, value: any) => {
    setFieldValue(`claimCondominium.${field}`, value);
  };

  const setSyndicField = (field: string, value: any) => {
    setFieldValue(`claimCondominium.syndic.${field}`, value);
  };

  const setInsuredFromCondominium = (
    field: 'document' | 'fullName' | 'email' | 'cellphone',
    value: string
  ) => {
    setFieldValue(`claimTenant.${field}`, value);
  };

  const handleCondominiumDocumentChange = (value: string) => {
    const maskedValue = maskCpfOrCnpj(value);
    setCondominiumField('document', maskedValue);
    setInsuredFromCondominium('document', maskedValue);
  };

  const handleCondominiumNameChange = (value: string) => {
    setCondominiumField('legalName', value);
    setInsuredFromCondominium('fullName', value);
  };

  const handleCondominiumEmailChange = (value: string) => {
    setCondominiumField('email', value);
    setInsuredFromCondominium('email', value);
  };

  const handleCondominiumPhoneChange = (value: string) => {
    const maskedValue = maskPhoneNumber(value);
    setCondominiumField('phone', maskedValue);
    setInsuredFromCondominium('cellphone', maskedValue);
  };

  return (
    <div>
      <SectionTitle>Informações do Condomínio</SectionTitle>
      <Grid columns={16}>
        <Grid.Row>
          <Grid.Column width={5}>
            <FieldLabel>CPF/CNPJ*</FieldLabel>
            <Field
              value={condominium.document}
              component={Input}
              fluid
              onChange={(_event: any, data: { value: string }) =>
                handleCondominiumDocumentChange(data.value)
              }
            />
          </Grid.Column>
          <Grid.Column width={11}>
            <FieldLabel>Razão social*</FieldLabel>
            <Field
              value={condominium.legalName}
              component={Input}
              fluid
              onChange={(_event: any, data: { value: string }) =>
                handleCondominiumNameChange(data.value)
              }
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={5}>
            <FieldLabel>Tipo*</FieldLabel>
            <DropdownNativo
              style={{ width: '100%' }}
              value={condominium.type || ''}
              options={condominiumTypeOptions}
              placeholder="Selecione"
              selection
              clearable
              onChange={(_event: SyntheticEvent, data: any) =>
                setCondominiumField('type', data.value || '')
              }
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <FieldLabel>Unidades</FieldLabel>
            <Field
              value={condominium.units || ''}
              component={Input}
              type="number"
              min={0}
              fluid
              onChange={(_event: any, data: { value: string }) =>
                setCondominiumField('units', data.value)
              }
            />
          </Grid.Column>
          <Grid.Column width={6}>
            <FieldLabel>Assessor</FieldLabel>
            <Field
              value={condominium.assessor || ''}
              component={Input}
              fluid
              onChange={(_event: any, data: { value: string }) =>
                setCondominiumField('assessor', data.value)
              }
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={5}>
            <FieldLabel>Telefone do condomínio</FieldLabel>
            <Field
              value={condominium.phone || ''}
              component={Input}
              fluid
              onChange={(_event: any, data: { value: string }) =>
                handleCondominiumPhoneChange(data.value)
              }
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <FieldLabel>E-mail do condomínio</FieldLabel>
            <Field
              value={condominium.email || ''}
              component={Input}
              fluid
              onChange={(_event: any, data: { value: string }) =>
                handleCondominiumEmailChange(data.value)
              }
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <SectionTitle>Informações do Síndico</SectionTitle>
      <Grid columns={16}>
        <Grid.Row>
          <Grid.Column width={5}>
            <FieldLabel>Nome do Síndico*</FieldLabel>
            <Field
              value={condominium.syndic.name}
              component={Input}
              fluid
              onChange={(_event: any, data: { value: string }) =>
                setSyndicField('name', data.value)
              }
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <FieldLabel>CPF/CNPJ</FieldLabel>
            <Field
              value={condominium.syndic.document || ''}
              component={Input}
              fluid
              onChange={(_event: any, data: { value: string }) =>
                setSyndicField('document', maskCpfOrCnpj(data.value))
              }
            />
          </Grid.Column>
          <Grid.Column width={6}>
            <FieldLabel>RG</FieldLabel>
            <Field
              value={condominium.syndic.rg || ''}
              component={Input}
              fluid
              onChange={(_event: any, data: { value: string }) =>
                setSyndicField('rg', data.value)
              }
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={5}>
            <FieldLabel>Data de Nascimento</FieldLabel>
            <Field
              value={condominium.syndic.birthDate || ''}
              component={Input}
              type="date"
              fluid
              onChange={(_event: any, data: { value: string }) =>
                setSyndicField('birthDate', data.value || null)
              }
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <FieldLabel>E-mail*</FieldLabel>
            <Field
              value={condominium.syndic.email}
              component={Input}
              type="email"
              fluid
              onChange={(_event: any, data: { value: string }) =>
                setSyndicField('email', data.value)
              }
            />
          </Grid.Column>
          <Grid.Column width={6}>
            <FieldLabel>Telefone*</FieldLabel>
            <Field
              value={condominium.syndic.phone}
              component={Input}
              fluid
              onChange={(_event: any, data: { value: string }) =>
                setSyndicField('phone', maskPhoneNumber(data.value))
              }
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <SectionTitle>Endereço do condomínio</SectionTitle>
      <Grid columns="equal">
        <Grid.Row>
          <Grid.Column width={3}>
            <FieldLabel>CEP*</FieldLabel>
            <Field
              value={values.claimProperty.zipCode}
              loading={loadingZipCode}
              component={Input}
              maxLength={9}
              fluid
              onChange={(_event: any, data: { value: string }) => onChangeCep(data.value)}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <FieldLabel>Logradouro*</FieldLabel>
            <Field
              value={values.claimProperty.street}
              component={Input}
              fluid
              onChange={(_event: any, data: { value: string }) =>
                setFieldValue('claimProperty.street', data.value)
              }
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <FieldLabel>Número*</FieldLabel>
            <Field
              value={values.claimProperty.number}
              component={Input}
              fluid
              onChange={(_event: any, data: { value: string }) =>
                setFieldValue('claimProperty.number', data.value)
              }
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={6}>
            <FieldLabel>Cidade*</FieldLabel>
            <Field
              value={values.claimProperty.city}
              component={Input}
              fluid
              onChange={(_event: any, data: { value: string }) =>
                setFieldValue('claimProperty.city', data.value)
              }
            />
          </Grid.Column>
          <Grid.Column width={6}>
            <FieldLabel>Bairro*</FieldLabel>
            <Field
              value={values.claimProperty.district}
              component={Input}
              fluid
              onChange={(_event: any, data: { value: string }) =>
                setFieldValue('claimProperty.district', data.value)
              }
            />
          </Grid.Column>
          <Grid.Column width={4}>
            <FieldLabel>UF*</FieldLabel>
            <DropdownNativo
              style={{ width: '100%' }}
              value={values.claimProperty.state || ''}
              options={stateOptions}
              placeholder="Selecione"
              selection
              clearable
              onChange={(_event: SyntheticEvent, data: any) =>
                setFieldValue('claimProperty.state', data.value || '')
              }
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={16}>
            <FieldLabel>Complemento</FieldLabel>
            <Field
              value={values.claimProperty.complement}
              component={Input}
              fluid
              onChange={(_event: any, data: { value: string }) =>
                setFieldValue('claimProperty.complement', data.value)
              }
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <SectionTitle>Informações do Seguro</SectionTitle>
      <Grid columns={16}>
        <Grid.Row>
          <Grid.Column width={5}>
            <FieldLabel>Seguradora/Garantidora</FieldLabel>
            <DropdownNativo
              style={{ width: '100%' }}
              value={values.claimEstate.insurerId || ''}
              options={selectableInsurers}
              placeholder="Selecione"
              search
              selection
              clearable
              onChange={(_event: SyntheticEvent, data: any) =>
                setFieldValue('claimEstate.insurerId', data.value || '')
              }
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <FieldLabel>Número da Apólice*</FieldLabel>
            <Field
              value={values.claimEstate.policyNumber || ''}
              component={Input}
              fluid
              onChange={(_event: any, data: { value: string }) =>
                setFieldValue('claimEstate.policyNumber', data.value || null)
              }
            />
          </Grid.Column>
          {!isEstateUser && selectableEstates.length > 0 && (
            <Grid.Column width={6}>
              <FieldLabel>Imobiliária*</FieldLabel>
              <DropdownNativo
                style={{ width: '100%' }}
                value={values.claimEstate.estateId || ''}
                options={selectableEstates}
                placeholder="Selecione"
                search
                selection
                clearable
                onChange={(_event: SyntheticEvent, data: any) =>
                  setFieldValue('claimEstate.estateId', data.value || '')
                }
              />
            </Grid.Column>
          )}
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={5}>
            <FieldLabel>Corretor Seguro*</FieldLabel>
            <Field
              value={values.claimEstate.insuranceBroker || ''}
              component={Input}
              fluid
              onChange={(_event: any, data: { value: string }) =>
                setFieldValue('claimEstate.insuranceBroker', data.value)
              }
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <FieldLabel>Status do Seguro</FieldLabel>
            <DropdownNativo
              style={{ width: '100%' }}
              value={values.claimEstate.insuranceStatus || ''}
              options={insuranceStatusOptions}
              placeholder="Selecione"
              search
              selection
              clearable
              onChange={(_event: SyntheticEvent, data: any) =>
                setFieldValue('claimEstate.insuranceStatus', data.value || '')
              }
            />
          </Grid.Column>
          <Grid.Column width={6}>
            <FieldLabel>Vencimento Seguro</FieldLabel>
            <Field
              value={values.claimEstate.insuranceExpirationDate || ''}
              component={Input}
              type="date"
              fluid
              onChange={(_event: any, data: { value: string }) =>
                setFieldValue('claimEstate.insuranceExpirationDate', data.value || null)
              }
            />
          </Grid.Column>
        </Grid.Row>
        {isEditMode && (
          <Grid.Row>
            <Grid.Column width={5}>
              <FieldLabel>STATUS</FieldLabel>
              <DropdownNativo
                style={{ width: '100%' }}
                value={values.claimEstate.status || ''}
                options={claimEstatesStatusEnum}
                placeholder="Selecione"
                selection
                clearable
                onChange={(_event: SyntheticEvent, data: any) =>
                  setFieldValue('claimEstate.status', data.value || '')
                }
              />
            </Grid.Column>
            {canSelectResponsible && (
              <Grid.Column width={5}>
                <FieldLabel>Responsável</FieldLabel>
                <DropdownNativo
                  style={{ width: '100%' }}
                  value={values.claimEstate.responsibleId || ''}
                  options={responsibleOptions}
                  placeholder="Selecione"
                  search
                  selection
                  clearable
                  onChange={(_event: SyntheticEvent, data: any) =>
                    setFieldValue('claimEstate.responsibleId', data.value || null)
                  }
                />
              </Grid.Column>
            )}
          </Grid.Row>
        )}
      </Grid>

      <SectionTitle>Contatos para notificação</SectionTitle>
      <Grid columns="16" style={{ paddingLeft: '1em' }}>
        {(values.claimEstate.notificationEmails as string[]).map(
          (notificationEmail, index) => (
            <Grid.Row key={`notification-email-${index}`}>
              <Grid.Column width={5}>
                <Field
                  value={notificationEmail}
                  component={Input}
                  type="email"
                  fluid
                  placeholder="E-mail*"
                  onChange={(_event: any, data: { value: string }) =>
                    setFieldValue(`claimEstate.notificationEmails[${index}]`, data.value)
                  }
                />
              </Grid.Column>
              <Grid.Column width={1}>
                <Icon
                  name="remove circle"
                  color="grey"
                  style={{ cursor: 'pointer', paddingTop: '5px' }}
                  size="large"
                  onClick={() => {
                    const emails = values.claimEstate.notificationEmails as string[];
                    if (emails.length === 1) {
                      setFieldValue('claimEstate.notificationEmails', ['']);
                    } else {
                      setFieldValue(
                        'claimEstate.notificationEmails',
                        emails.filter((_email, emailIndex) => emailIndex !== index)
                      );
                    }
                  }}
                />
              </Grid.Column>
              {index + 1 === values.claimEstate.notificationEmails?.length && (
                <Grid.Column width={5}>
                  <Button
                    onClick={() => {
                      const emails = values.claimEstate.notificationEmails as string[];
                      if (emails[emails.length - 1]?.length > 0) {
                        setFieldValue('claimEstate.notificationEmails', [...emails, '']);
                      }
                    }}
                  >
                    <Icon name="plus circle" />
                    Adicionar E-mail
                  </Button>
                </Grid.Column>
              )}
            </Grid.Row>
          )
        )}
      </Grid>
    </div>
  );
};

export default CondominiumBasicInfoStep;
